const RECENT_KEY = 'nallan:recently-viewed';
const RECENT_LIMIT = 4;
// Rendered cards are kept for a day, so the next time the search opens they appear at once.
const CARD_CACHE_KEY = 'nallan:recent-cards';
const CARD_CACHE_TTL = 24 * 60 * 60 * 1000;

function readCardCache() {
  try {
    const cache = JSON.parse(localStorage.getItem(CARD_CACHE_KEY)) || {};
    const now = Date.now();
    return Object.fromEntries(Object.entries(cache).filter(([, entry]) => now - entry.time < CARD_CACHE_TTL));
  } catch {
    return {};
  }
}

function writeCardCache(cache) {
  try {
    localStorage.setItem(CARD_CACHE_KEY, JSON.stringify(cache));
  } catch {}
}

function readRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
  } catch {
    return [];
  }
}

function writeRecent(handles) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(handles));
  } catch {}
}

const viewedHandle = document.body.dataset.productHandle;
if (viewedHandle) {
  writeRecent([viewedHandle, ...readRecent().filter((handle) => handle !== viewedHandle)].slice(0, 12));
  // Save this product's card now, so it shows at once in "Recently viewed" on the next page.
  const own = document.querySelector('template[data-search-card-self]')?.content.querySelector('.search-card');
  if (own) writeCardCache({ ...readCardCache(), [viewedHandle]: { html: own.outerHTML, time: Date.now() } });
}

// Cards for products viewed before this was in place are fetched once, in the background after load.
function cardUrl(root, handle) {
  return `${root}/products/${encodeURIComponent(handle)}?section_id=search-product-card`;
}

async function fetchCard(root, handle) {
  try {
    const response = await fetch(cardUrl(root, handle));
    if (!response.ok) return null;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return doc.querySelector('.search-card');
  } catch {
    return null;
  }
}

async function warmRecentCards(root) {
  const cache = readCardCache();
  const missing = readRecent()
    .filter((handle) => handle !== viewedHandle && !cache[handle])
    .slice(0, RECENT_LIMIT);
  if (!missing.length) return;
  await Promise.all(
    missing.map(async (handle) => {
      const card = await fetchCard(root, handle);
      if (card) cache[handle] = { html: card.outerHTML, time: Date.now() };
    })
  );
  writeCardCache({ ...readCardCache(), ...cache });
}

class SearchModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.defaultView = this.querySelector('[data-search-default]');
    this.recentGroup = this.querySelector('[data-recent]');
    this.recentList = this.querySelector('[data-recent-list]');
    this.root = (this.dataset.rootUrl || '/').replace(/\/$/, '');

    this.onDocumentClick = (event) => {
      const trigger = event.target.closest('[data-search-open]');
      if (!trigger || this.contains(trigger)) return;
      event.preventDefault();
      this.open(trigger);
    };

    this.onDocumentFocus = (event) => {
      if (!event.target.matches('[data-search-open] input')) return;
      event.target.blur();
      this.open();
    };

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('focusin', this.onDocumentFocus);

    // The × first clears what was typed; with an empty field it closes the search.
    this.querySelector('[data-search-close]').addEventListener('click', () => {
      if (this.input.value) {
        this.input.value = '';
        clearTimeout(this.debounce);
        this.search('');
        this.input.focus();
        return;
      }
      this.close();
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    // iPhone: nothing around the panel may move with the finger. Only the results list scrolls,
    // and only when it has more than fits; otherwise the touch does nothing.
    this.dialog.addEventListener(
      'touchmove',
      (event) => {
        const list = event.target.closest?.('.search-modal__body');
        if (list && list.scrollHeight > list.clientHeight + 1) return;
        event.preventDefault();
      },
      { passive: false }
    );

    // Scrolling on the dimmed page behind the panel closes the search, after a short drag
    // (so a light touch doesn't). Scrolling inside the results keeps it open.
    const outside = (event) => event.target === this.dialog;
    this.dialog.addEventListener(
      'touchstart',
      (event) => {
        this.dragStart = outside(event) ? event.touches[0].clientY : null;
      },
      { passive: true }
    );
    this.dialog.addEventListener(
      'touchmove',
      (event) => {
        if (this.dragStart == null) return;
        if (Math.abs(event.touches[0].clientY - this.dragStart) > 100) {
          this.dragStart = null;
          this.close({ soft: true });
        }
      },
      { passive: true }
    );
    this.dialog.addEventListener(
      'wheel',
      (event) => {
        if (!outside(event)) return;
        this.wheelDistance = (this.wheelDistance || 0) + Math.abs(event.deltaY);
        if (this.wheelDistance > 120) this.close({ soft: true });
      },
      { passive: true }
    );
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.querySelector('[data-recent-clear]').addEventListener('click', () => {
      writeRecent([]);
      this.recentGroup.hidden = true;
    });

    this.input.addEventListener('input', () => {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.search(this.input.value), 250);
    });

    const warm = () => warmRecentCards(this.root);
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(warm, { timeout: 4000 }) : setTimeout(warm, 2000));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('focusin', this.onDocumentFocus);
  }

  open(trigger) {
    if (this.dialog.open) return;
    const anchor = trigger?.closest('[data-search-anchor]');
    const header = document.querySelector('sticky-header');
    let top = header ? Math.max(header.getBoundingClientRect().bottom, 0) : 0;
    if (anchor) top = Math.max(anchor.getBoundingClientRect().top, 0);
    this.dialog.classList.toggle('is-anchored', Boolean(anchor));
    this.dialog.style.setProperty('--search-top', `${top}px`);
    this.dialog.showModal();
    document.documentElement.classList.add('is-searching');
    this.lockPage();
    this.input.focus();
    this.renderRecent();
  }

  // iPhone ignores overflow: hidden, so the page kept moving (and re-laying out) behind the panel.
  // Pinning the body at its current scroll position is the lock iOS respects; the position is
  // put back on close. The scrollbar's width is kept as padding so desktop content doesn't shift.
  lockPage() {
    if (this.locked) return;
    const body = document.body;
    this.lockedScroll = window.scrollY;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    body.style.position = 'fixed';
    body.style.top = `-${this.lockedScroll}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
    this.locked = true;
  }

  unlockPage() {
    if (!this.locked) return;
    const body = document.body;
    ['position', 'top', 'left', 'right', 'width', 'paddingRight'].forEach((prop) => (body.style[prop] = ''));
    window.scrollTo({ top: this.lockedScroll, behavior: 'instant' });
    this.locked = false;
  }

  // soft: the slower fade used when a swipe on the dark area closes the search.
  close({ soft = false } = {}) {
    if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
    this.wheelDistance = 0;
    this.input.blur();
    document.documentElement.classList.remove('is-searching');
    const finish = () => {
      this.dialog.classList.remove('is-closing', 'is-closing-soft');
      this.dialog.close();
      this.unlockPage();
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return finish();
    this.dialog.classList.toggle('is-closing-soft', soft);
    this.dialog.classList.add('is-closing');
    const onEnd = (event) => {
      if (event.target !== this.dialog) return;
      this.dialog.removeEventListener('animationend', onEnd);
      clearTimeout(fallback);
      finish();
    };
    // In case the animation never reports back (e.g. tab hidden), close anyway.
    const fallback = setTimeout(() => {
      this.dialog.removeEventListener('animationend', onEnd);
      finish();
    }, soft ? 700 : 450);
    this.dialog.addEventListener('animationend', onEnd);
  }

  async search(term) {
    this.controller?.abort();
    const query = term.trim();

    if (!query) {
      this.results.replaceChildren();
      this.defaultView.hidden = false;
      return;
    }

    this.controller = new AbortController();
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });

    this.results.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${this.dataset.predictiveUrl}?${params}`, { signal: this.controller.signal });
      if (!response.ok) throw new Error(response.status);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = doc.querySelector('[data-predictive-results]');
      this.results.replaceChildren(...(content ? [content] : []));
      this.defaultView.hidden = true;
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('[search]', error);
    } finally {
      this.results.removeAttribute('aria-busy');
    }
  }

  // The group takes its place at once (cached cards, or card-sized placeholders), so the
  // "Products" below never jumps when the cards arrive; each card then fades in where it belongs.
  async renderRecent() {
    const handles = readRecent()
      .filter((handle) => handle !== viewedHandle)
      .slice(0, RECENT_LIMIT);

    if (handles.length === 0) {
      this.recentGroup.hidden = true;
      return;
    }

    const cache = readCardCache();
    const items = handles.map((handle) => {
      const item = document.createElement('li');
      if (cache[handle]) {
        item.innerHTML = cache[handle].html;
      } else {
        item.className = 'search-modal__placeholder';
        item.setAttribute('aria-hidden', 'true');
      }
      return item;
    });
    this.recentList.replaceChildren(...items);
    this.recentGroup.hidden = false;
    const run = (this.recentRun = (this.recentRun || 0) + 1);

    await Promise.all(
      handles.map(async (handle, index) => {
        if (cache[handle]) return;
        const card = await fetchCard(this.root, handle);
        if (run !== this.recentRun) return;
        const item = items[index];
        if (!card) {
          item.remove();
          return;
        }
        cache[handle] = { html: card.outerHTML, time: Date.now() };
        item.className = 'is-loaded';
        item.removeAttribute('aria-hidden');
        item.replaceChildren(card);
      })
    );

    if (run !== this.recentRun) return;
    writeCardCache(cache);
    this.recentGroup.hidden = this.recentList.children.length === 0;
  }
}

if (!customElements.get('search-modal')) customElements.define('search-modal', SearchModal);
